import {useMutation} from '@apollo/react-hooks';
import type {FetchResult} from 'apollo-link';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    SharedEmailAddressRemove as RemoveSharedEmailAddressMutation,
    SharedEmailAddressRemoveVariables as RemoveSharedEmailAddressMutationVariables,
    SharedEmailAddressRemoveInput,
    GetSharedInboxConfig as GetSharedInboxConfigQuery,
} from 'nutshell-graphql-types';

import SHARED_EMAIL_ADDRESS_REMOVE from '../mutations/remove-shared-email-address.graphql';
import GET_SHARED_INBOX_CONFIG from '../queries/get-shared-inbox-config.graphql';

interface UseRemoveSharedEmailAddressReturn {
    removeSharedEmailAddress: (
        input: SharedEmailAddressRemoveInput
    ) => Promise<FetchResult<RemoveSharedEmailAddressMutation>>;
    isLoading: boolean;
    error: string | null | undefined;
}

export const useRemoveSharedEmailAddress = (): UseRemoveSharedEmailAddressReturn => {
    const [removeSharedEmailAddress, {loading, error}] = useMutation<
        RemoveSharedEmailAddressMutation,
        RemoveSharedEmailAddressMutationVariables
    >(SHARED_EMAIL_ADDRESS_REMOVE);

    return {
        removeSharedEmailAddress: (input: SharedEmailAddressRemoveInput) => {
            return removeSharedEmailAddress({
                variables: {input},
                update: (cache) => {
                    const existingQueryResult = cache.readQuery<GetSharedInboxConfigQuery>({
                        query: GET_SHARED_INBOX_CONFIG,
                    });

                    if (!existingQueryResult?.sharedInboxConfig) {
                        return;
                    }

                    const existingAddresses = existingQueryResult.sharedInboxConfig.sharedAddresses;
                    const cacheContainsRemovedAddress = existingAddresses.some(
                        (address) => address.id === input.fromAddressId
                    );
                    if (!cacheContainsRemovedAddress) {
                        return;
                    }

                    const newAddresses = existingAddresses.filter(
                        (address) => address.id !== input.fromAddressId
                    );

                    cache.writeQuery<GetSharedInboxConfigQuery>({
                        query: GET_SHARED_INBOX_CONFIG,
                        data: {
                            sharedInboxConfig: {
                                ...existingQueryResult.sharedInboxConfig,
                                sharedAddresses: [...newAddresses],
                            },
                        },
                    });
                },
            });
        },
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading: loading,
    };
};
